import { reducedMotionQuery } from "./motion";
import type { Teardown } from "./registry";

export type Player = {
	play: () => void;
	pause: () => void;
	onPlaying: (callback: () => void) => void;
	teardown: () => void;
};

/** Dispatched on the media, and bubbling, each time playback starts while it is not paused. */
export const STARTED_EVENT = "fa-video-started";

const SCOPE = "[data-fa-video-scope]";
const PAUSE = "[data-fa-video-pause]";
const READY = "fa-video-pause--ready";

/** Binds the pause controls sharing `media`'s nearest `data-fa-video-scope`. Starts paused for a
    reader who asked for less motion. */
export const controlPlayback = (
	media: Element,
	makePlayer: (signal: AbortSignal) => Player,
): Teardown => {
	const listeners = new AbortController();
	const { signal } = listeners;
	const player = makePlayer(signal);
	const buttons = [...(media.closest(SCOPE)?.querySelectorAll<HTMLButtonElement>(PAUSE) ?? [])];
	const authoredPressed = buttons.map(
		(button) => [button, button.getAttribute("aria-pressed")] as const,
	);
	const reduced = reducedMotionQuery();
	let paused = reduced.matches;

	const setPaused = (next: boolean) => {
		paused = next;
		for (const button of buttons) button.setAttribute("aria-pressed", String(paused));
		if (paused) player.pause();
		else player.play();
	};

	/* Another script can start playback after a pause: SponsorBlock replays a chosen panel's video. */
	player.onPlaying(() => {
		if (paused) player.pause();
		else media.dispatchEvent(new Event(STARTED_EVENT, { bubbles: true }));
	});

	for (const button of buttons) {
		button.addEventListener("click", () => setPaused(!paused), { signal });
		button.classList.add(READY);
	}

	reduced.addEventListener("change", () => reduced.matches && setPaused(true), { signal });

	setPaused(paused);

	return () => {
		listeners.abort();
		player.teardown();

		for (const [button, pressed] of authoredPressed) {
			button.classList.remove(READY);
			if (pressed === null) button.removeAttribute("aria-pressed");
			else button.setAttribute("aria-pressed", pressed);
		}
	};
};
